import {
  calclulateCombinedMultiInvestment,
  calculateMultiProjectedPay,
  calculateMultiReturn,
} from '@mobi/betslip/helpers/calculator/multis'
import { isFobSelection, isToteSelection } from '@mobi/betslip/helpers/typeGuards'
import { calculateEstReturnSingleItem } from '@mobi/betslip/helpers/calculator/singles'
import { selectMultiInvestment } from '@mobi/betslip/Store/Bets/selectors'
import { store } from '@core/Store'
import { keys } from '@classic/Foundation/Analytics/AnalyticsDataLayer'
import { trackEvent } from '@classic/Foundation/Analytics/GoogleTagManagerService'
import type { QuickbetShare, ShareBetData } from '@core/Areas/ShareBet/types'
import {
  isBetSlipShareData,
  isQuickbetShareData,
  isTicketShareData,
} from '../../helpers/typeGuards'

export function trackShareBetClick(
  data: ShareBetData,
  shouldShowCost: boolean,
  destination?: 'external' | 'follow-me'
) {
  if (isTicketShareData(data)) {
    trackShareBetClickEvent({
      location: 'transacted-bets',
      product: data.share.ticket.productName,
      spend: data.share.ticket.investment.total,
      payout: data.share.ticket.return,
      betStatus: data.share.ticket.status,
      costShared: shouldShowCost,
      odds: data.share.ticket.combinedDividend,
      destination,
    })
    return
  }

  let spend: number | undefined
  let payout: number | undefined
  let odds: number | null | undefined

  const isMultiBet = data.share.items.length > 1
  const items = data.share.items

  const isBetPlaced =
    (isBetSlipShareData(data) && data.share.isBetPlaced) || isQuickbetShareData(data)

  if (isBetPlaced) {
    if (isMultiBet) {
      const multiInvestment = selectMultiInvestment(store.getState())
      spend = calclulateCombinedMultiInvestment(items, multiInvestment)
      payout = calculateMultiProjectedPay(items, multiInvestment)
      odds = calculateMultiReturn(items)
    } else {
      const winInvestment = items[0].investment.win.value
      const placeInvestment = items[0].investment.place.value
      spend = winInvestment + placeInvestment
      payout = calculateEstReturnSingleItem(items[0])
      odds = isFobSelection(items[0].selection)
        ? winInvestment
          ? items[0].selection.winPrice
          : items[0].selection.placePrice
        : undefined
    }
  }

  if (isBetSlipShareData(data)) {
    trackShareBetClickEvent({
      location: 'bet-slip',
      product: isMultiBet ? 'multi' : 'single',
      spend,
      payout,
      odds,
      betStatus: data.share.isBetPlaced ? 'pending' : 'unplaced',
      costShared: shouldShowCost,
      destination,
    })
    return
  }

  if (isQuickbetShareData(data)) {
    trackShareBetClickEvent({
      location: 'quickbet',
      product: quickBetProduct(data),
      spend: data.share.betCost ? data.share.betCost : spend,
      payout,
      odds: isToteSelection(items[0].selection) ? null : odds,
      betStatus: 'Pending',
      costShared: shouldShowCost,
      destination,
    })
  }
}

// =============
// Local Helpers
// =============

function trackShareBetClickEvent(data: {
  product: string | undefined
  spend: number | null | undefined
  payout: number | null | undefined
  betStatus: string
  costShared: boolean
  location: string
  odds: number | null | undefined
  destination?: 'external' | 'follow-me'
}) {
  trackEvent(keys.shareBet, data)
}

function quickBetProduct(data: QuickbetShare): string | undefined {
  const selection = data.share.items[0].selection

  if ('betType' in selection) {
    return selection.betType
  } else if ('type' in selection) {
    return selection.type
  }

  return undefined
}
